import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'
import { useGSAP } from '@gsap/react'

/**
 * The ONLY module of the site that imports GSAP. Every motion hook takes its
 * primitives and media queries from here, so the plugin set, the reduced-
 * motion split and the desktop breakpoint are declared once.
 *
 * Callbacks: GSAP (≥ 3.12) runs tween / timeline / delayedCall callbacks and
 * ScrollTrigger callbacks (batch included — it drives them via a delayedCall)
 * inside the gsap.context that created them (gsap-core `_callback`,
 * ScrollTrigger `_callback` → `self._ctx.add`). So the tweens the hooks create
 * from `onEnter` / `onToggle` / `onSplit` are reverted with their matchMedia
 * context without a contextSafe() wrapper; contextSafe is for non-GSAP async
 * callbacks (DOM events, promises), and no hook creates GSAP objects there.
 */

export { gsap, ScrollTrigger, SplitText, useGSAP }

let registered = false

/**
 * Registers the plugins once, on the client only. Every hook calls it first
 * thing in its useGSAP callback (a layout effect: after hydration, before any
 * tween) — never at import:
 * - the SSR bundle imports this module while rendering, and plugins must never
 *   touch window/document then (the `typeof window` guard also makes a stray
 *   server-side call a no-op);
 * - a top-level `gsap.registerPlugin(…)` is a side effect Rollup must keep in
 *   every bundle that merely imports this module, and App.tsx imports all the
 *   sites statically (the others are dropped only by constant folding), so
 *   Verde Vercelli and Verde Valsesia would ship GSAP + ScrollTrigger too.
 *   Called from inside the hooks, it is tree-shaken with them.
 */
export function registerMotion(): void {
  if (registered || typeof window === 'undefined') return
  gsap.registerPlugin(ScrollTrigger, SplitText, useGSAP)
  registered = true
}
export const REDUCED = '(prefers-reduced-motion: reduce)'
export const NO_PREF = '(prefers-reduced-motion: no-preference)'
export const DESKTOP = '(min-width: 1024px)'
